import { resolveSelectedVersionIdAfterFetch } from "@/components/MedicalChronology/contexts/medicalChronologyVersionUtils";
import type {
  ChronologyVersion,
  ChronologyVersionResponse,
} from "@/components/MedicalChronology/types";
import { useAPI } from "@/hooks/useAPI";
import type { MedChronService } from "@/services/medChronService";
import { formatDate } from "@/utils";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-hot-toast";

interface UseMedicalChronologyVersionsParams {
  caseId: string;
  medChronService: MedChronService;
}

export const getVersionDisplayName = (version: ChronologyVersionResponse | null): string => {
  if (!version) return "";
  const formattedDate = formatDate(version.created_at);
  if (!formattedDate) return version.version_name;
  return `${version.version_name} - ${formattedDate}`;
};

export const useMedicalChronologyVersions = ({
  caseId,
  medChronService,
}: UseMedicalChronologyVersionsParams) => {
  const api = useAPI();
  const [versions, setVersions] = useState<ChronologyVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [isVersionActionLoading, setIsVersionActionLoading] = useState(false);

  const currentVersion = useMemo(() => versions.find((v) => v.is_current) ?? null, [versions]);

  const fetchVersions = useCallback(async () => {
    try {
      const data = await medChronService.getChronologyVersions(caseId);
      const nextVersions = (data as ChronologyVersionResponse[]).map((version) => ({
        ...version,
        displayName: getVersionDisplayName(version),
      }));
      setVersions(nextVersions);
      setSelectedVersionId((previousId) =>
        resolveSelectedVersionIdAfterFetch(nextVersions, previousId)
      );
      return nextVersions;
    } catch (error) {
      console.error("Error fetching versions:", error);
      toast.error("Error loading versions");
      throw error;
    }
  }, [caseId, medChronService]);

  const setSelectedVersionIdValidated = useCallback(
    (versionId: string) => {
      if (versions.some((v) => v.version_id === versionId)) {
        setSelectedVersionId(versionId);
      }
    },
    [versions]
  );

  const updateVersionName = useCallback(
    async (versionId: string, versionName: string) => {
      await medChronService.updateVersionName(versionId, versionName);
      await fetchVersions();
    },
    [fetchVersions, medChronService]
  );

  const setVersionAsCurrent = useCallback(
    async (versionId: string) => {
      await medChronService.setVersionAsCurrent(versionId);
      await fetchVersions();
    },
    [fetchVersions, medChronService]
  );

  const duplicateVersion = useCallback(
    async (versionId: string) => {
      setIsVersionActionLoading(true);
      try {
        await api.post(`/chronology/versions/${versionId}/duplicate`);
        await fetchVersions();
      } finally {
        setIsVersionActionLoading(false);
      }
    },
    [api, fetchVersions]
  );

  const deleteVersion = useCallback(
    async (versionId: string) => {
      setIsVersionActionLoading(true);
      try {
        await api.delete(`/chronology/versions/${versionId}`);
        await fetchVersions();
      } finally {
        setIsVersionActionLoading(false);
      }
    },
    [api, fetchVersions]
  );

  return {
    versions,
    selectedVersionId,
    currentVersion,
    isVersionActionLoading,
    fetchVersions,
    setSelectedVersionId: setSelectedVersionIdValidated,
    updateVersionName,
    setVersionAsCurrent,
    duplicateVersion,
    deleteVersion,
  };
};
